import axios from 'axios';
import ENDPOINTS from 'shared/api/endpoints';
import UrlModifier from 'shared/utils/url';
import { createVirtualCoreSession } from 'features/session/virtualCoreSession';
import RequestClient from 'shared/api/http-client';
import { getPlatformApiBaseUrl, requirePlatformApiBaseUrl } from 'platform/modules/session/services/platformApiConfig';

import type { PlatformValidatedUser, SessionUser } from './types';

/** Error code used when session is valid but user/customer does not have virtual_sports enabled. */
export const VIRTUAL_NOT_ALLOWED_CODE = 'VIRTUAL_NOT_ALLOWED';

export class VirtualNotAllowedError extends Error {
  code = VIRTUAL_NOT_ALLOWED_CODE;
  constructor(message: string) {
    super(message);
    this.name = 'VirtualNotAllowedError';
  }
}

function isAxiosError(e: unknown): e is { response?: { status: number; data?: { message?: string } } } {
  return typeof e === 'object' && e !== null && 'response' in e;
}

export async function validatePlatformSession(params: {
  sessionId: string;
}): Promise<PlatformValidatedUser> {
  const { sessionId } = params;

  const baseDomain = requirePlatformApiBaseUrl();
  const validateUrl = `${baseDomain}${ENDPOINTS.PLATFORM.SESSION_VALIDATE}`;
  const client = axios.create({ withCredentials: true });

  try {
    const resp = await client.get(validateUrl, {
      withCredentials: true,
      headers: { 'session-id': sessionId },
    });
    const data = resp.data as { valid?: boolean; user?: PlatformValidatedUser; message?: string };

    if (!data?.valid || !data?.user) {
      throw new Error(data?.message || 'Session validation failed');
    }

    return data.user;
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 403) {
      const message = err.response?.data?.message ?? String(err);
      throw new VirtualNotAllowedError(message);
    }
    // 401/404 = session not found or expired; rethrow so bootstrap fails and host can be notified via c7:auth:session-invalid
    throw err;
  }
}

/**
 * Validate platform session using the session cookie (no session_id in query).
 * Used when virtual-web loads without s= in URL (e.g. refresh or cross-entry); browser sends cookie with credentials.
 * Returns session_id and user when valid, null when no/invalid session.
 */
export async function validatePlatformSessionFromCookie(): Promise<{
  session_id: string;
  user: PlatformValidatedUser;
} | null> {
  const baseDomain = getPlatformApiBaseUrl();
  if (!baseDomain) {
    return null;
  }

  const validateUrl = `${baseDomain}${ENDPOINTS.PLATFORM.SESSION_VALIDATE}`;
  const client = axios.create({ withCredentials: true });

  try {
    const resp = await client.get(validateUrl, { withCredentials: true });
    const data = resp.data as {
      valid?: boolean;
      session_id?: string;
      user?: PlatformValidatedUser;
      message?: string;
    };

    if (!data?.valid || !data?.user || !data?.session_id) {
      return null;
    }

    return {
      session_id: String(data.session_id),
      user: data.user
    };
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 403) {
      const message = err.response?.data?.message ?? String(err);
      throw new VirtualNotAllowedError(message);
    }
    return null;
  }
}

export async function createVirtualSessionId(params: {
  platformSessionId: string;
  userData: PlatformValidatedUser;
}): Promise<string> {
  const { platformSessionId, userData } = params;
  const virtualCoreSession = await createVirtualCoreSession(platformSessionId, userData);
  if (!virtualCoreSession || !virtualCoreSession.session_id) {
    throw new Error('Failed to create virtual-core session');
  }
  return String(virtualCoreSession.session_id);
}

export async function fetchVirtualSession(params: { virtualSessionId: string }): Promise<SessionUser> {
  const { virtualSessionId } = params;
  const url = UrlModifier.bindToUrl(ENDPOINTS.SESSIONS_READ, { id: virtualSessionId });
  if (!url || typeof url !== 'string') throw new Error('Invalid API endpoint URL');
  const resp = await RequestClient.get(url);
  const data = resp.data;
  if (data === undefined) throw new Error('API response missing data');
  return data as SessionUser;
}

export async function fetchGuestConfig(params: {
  language: string;
  timezone: string;
  currency: string;
  odds_format: string;
  domain?: string;
}): Promise<SessionUser> {
  const { language, timezone, currency, odds_format, domain } = params;

  const apiUrl = process.env.REACT_APP_API_URL;
  if (!apiUrl) {
    throw new Error('Missing REACT_APP_API_URL for guest config');
  }
  const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
  const configUrl = `${baseUrl}${ENDPOINTS.GUEST_CONFIG}`;

  const resp = await RequestClient.get(configUrl, {
    params: domain ? { domain } : undefined,
  });
  const config = (resp.data || {}) as Record<string, unknown>;

  const has_decimals =
    typeof config.has_decimals !== 'undefined'
      ? Number(config.has_decimals)
      : odds_format.toLowerCase() === 'eu'
        ? 1
        : 0;

  return {
    guest: true,
    skin_id: config.skin_id ?? null,
    language: (config.language as string) || language,
    timezone: (config.timezone as string) || timezone,
    currency: (config.currency as string) || currency,
    odds_format: (config.odds_format as string) || odds_format,
    has_decimals,
    player_balance: 0,
    session_id: null,
    player_id: null,
  };
}

